import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { VetRuleOutcomeStepEnum, VetStatusEnum, CustomerDecisionStatusEnum, VettingType } from "../../../../../../../../models/vetting/vetting.models";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../../../configuration/rightshipqi-router.constants";
import { LookupValue, LookupValueRequest } from "../../../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../../../../../services/lookups/lookup-client.service";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ManageVesselService } from "../../../../../../../../services/vessels/manage-vessel.service";
import { AutoScreeningService } from "../../../../../../../../services/vetting/auto-screening.service";
import { BaseVetOverview } from "../../../../../vetting/steps/overview/base-vet-overview";
import { VettingService } from "../../../../../../../../services/vetting/vetting.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../../../client-validation-system/validations.models";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../../../client-validation-system/validations-service";
import { VesselDetailModel } from "../../../../../../../../models/vessels/vessel.models";
import { UserClientService } from "../../../../../../../../services/authentication/user-client.service";
import { DateHelper } from "../../../../../../../../utilities/date-helper";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { CustomerSubscriptionService } from "../../../../../../../../services/modules/customer-subscription-client.service";
import { CustomerVesselService } from "../../../../../../../../services/vessels/customer-vessels.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject
@transient()
@useView('views/pages/modules/auto-screening/templates/trade-check/tabs/overview/trade-check-overview.html')
export class TradeCheckOverview extends BaseVetOverview {

    private vetStatuses: LookupValue[]; 
    public canProcessAutoScreeningCustomerDecision: boolean;
    public canProcessScreeningCustomerDecisionForSubscriptions: boolean;
    public canProcessScreeningCustomerDecisionForRightStore: boolean;
    public unacceptable: VetStatusEnum = VetStatusEnum.AS_Failed;
    public canLinkAutoScreeningWithFullVet: boolean;
    public canViewRiskScoresInScreeningOverview: boolean;
    public isRightShipUser: boolean;
    validator: ClientSideValidator;
 
    selectedOutcomes: number[] = [];

    completed(statusId: number) {
        return statusId == VetStatusEnum.AS_Passed ||
            statusId == VetStatusEnum.AS_Failed;
    }
 
    @computedFrom("item", "item.vetStatusId")
    get showCustomerDecision() {
        return this.item && this.item.vetStatusId == VetStatusEnum.AS_ExceptionRequired;
    }

    @computedFrom("item", "item.vetStatusId", "item.customerDecisionStatusId")
    get customerDecisionStatus() {
        if (this.item) {
            if (!this.item.requireCustomerDecision && !this.showCustomerDecision) return "";

            if (this.item.customerDecisionStatusId) {
                return this.getDecisionStatus(this.item.customerDecisionStatusId);
            }
        }
        return "Pending";
    }

    @computedFrom("item", "item.vetStatusId", "item.previousCustomerDecisionStatusId")
    get previousCustomerDecisionStatus() {
        if (this.item && this.item.previousCustomerDecisionStatusId) {
            return this.getDecisionStatus(this.item.previousCustomerDecisionStatusId);
        }
        return "";
    }

    @computedFrom("lastVetStatusHistory", "lastVetStatusHistory.user")
    get lastVetStatusHistoryUsers() {
        const systemStatuses = [
            VetStatusEnum.Processing,
            VetStatusEnum.ProcessingOutcome
        ];
        const users = this.lastVetStatusHistories.map(x => {
            let user = x.user;
            if (!user && (systemStatuses.indexOf(x.statusId) > -1
                || (x.isAutoCancelled == true))) user = "System";
            return user;
        });
        return users;
    }

    @computedFrom("item", "item.vetStatusHistory", "item.vetStatusHistory.length", "item.vetStatusId")
    get lastVetStatusHistories() {
        const list = [this.item.vetStatusHistory[0]];

        if (this.showCustomerDecision && this.item.vetStatusHistory.length > 1 && this.item.vetStatusHistory[1].statusId == VetStatusEnum.AS_Failed)
            list.push(this.item.vetStatusHistory[1]);

        return list;
    }

    @computedFrom("this.item.crewWelfareValidUntilAtVetOutcome")
    get daysUntilCrewWelfareExpires(): number {
        const today = new Date();
        return DateHelper.daysBetweenDates(today.toString(), this.item.crewWelfareValidUntilAtVetOutcome.toString())
    }
    
    @computedFrom("this.item.crewWelfareStatusAtVetOutcome", "this.daysUntilCrewWelfareExpires")
    get isCrewWelfareExpiring(): boolean {
        const expiringCounterLength: number = 90;

        if(this.item.crewWelfareStatusAtVetOutcome == 'Submitted'){
            if(this.daysUntilCrewWelfareExpires < expiringCounterLength)
                return true
        }
    }

    @computedFrom(Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks),
    Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks.length))
    get failedCriticalCount() {
        return this.item.vetTasks.filter(x => x.isCritical && (x.vetTaskOutcomeId != VetRuleOutcomeStepEnum.Pass || x.systemError)).length;
    }

    @computedFrom(Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks),
    Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks.length))
    get criticalCount() {
        return this.item.vetTasks.filter(x => x.isCritical).length;
    }
 
    @computedFrom(Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks),
    Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks.length)) 
    get failedSoftCount() {
        return this.item.vetTasks.filter(x => !x.isCritical && (x.vetTaskOutcomeId != VetRuleOutcomeStepEnum.Pass || x.systemError)).length;
    }

    @computedFrom(Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks),
    Reflection.getPropertyPath<TradeCheckOverview>(z => z.item.vetTasks.length))
    get softCount() {
        return this.item.vetTasks.filter(x => !x.isCritical).length;
    } 

    get vetStatus() {
        if (this.vetStatuses && this.item)
            return this.vetStatuses.filter(x => x.id == this.item.vetStatusId)[0];

        return null;
    } 
 
    get lastVetStatusHistoryEscalated() {
        return this.lastVetStatusHistories.filter(x => x.statusId == VetStatusEnum.AS_ExceptionRequired)[0];
    }
    constructor(private vettingClientService: AutoScreeningService, signalClientService: SignalClientService, manageVesselService: ManageVesselService,
        notificationsService: NotificationClientService, lookupClientService: LookupClientService, vettingService: VettingService,
        userClaimsService: UserClaimsService, dialogClientService: DialogClientService, private entityValidationsService: EntityValidationsService,
        i18N: I18N, router: Router, userClientService: UserClientService, configService: ConfigService,
        customerSubscriptionService: CustomerSubscriptionService,
        customerVesselsService: CustomerVesselService
    ) {
        super(notificationsService, i18N, vettingService, dialogClientService, userClaimsService, signalClientService,
            manageVesselService, router, userClientService,
            customerSubscriptionService, customerVesselsService, configService);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VetStatuses;
        request.parameters = "1,2";
        lookupClientService.getLookup(request).then(x => {
            this.vetStatuses = x.activeValues;
        });
        this.canProcessAutoScreeningCustomerDecision = this.userClaimsService.hasPermission(Claims.CanProcessAutoScreeningCustomerDecision);
        this.canProcessScreeningCustomerDecisionForRightStore = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForRightStore);
        this.canProcessScreeningCustomerDecisionForSubscriptions = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForSubscriptions);
        this.canLinkAutoScreeningWithFullVet = this.userClaimsService.hasPermission(Claims.CanLinkAutoScreeningWithFullVet);
        this.isAutoScreen = true;

        this.isRightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
    }

    async setCustomerDecision(customerDecisionStatusId: number) {
        this.setValidator();
        const valid = await this.validator.validate();
        if (valid.valid) {
            try {
                const res = await this.vettingClientService.setCustomerDecision(this.item.vetId, customerDecisionStatusId, this.decisionComments)
                this.item.customerDecisionComment = res.comments;
                this.item.customerDecisionStatusDate = res.date;
                this.item.customerDecisionStatusBy = res.username;
                this.item.customerDecisionStatusId = customerDecisionStatusId;
                this.notificationService.show("Customer Decision", "Successfully saved", NotificationType.Success);
            } catch {
                this.notificationService.show("Customer Decision", "Failed to set customer decision", NotificationType.Error);
            }
        }
    }

    protected onPermissionsChanged = (userResponse) =>
    {
        return new Promise<void>((resolve) => {
            if(userResponse && userResponse.allowedClaims)
            {
                this.userClaimsService.setUserClaims(userResponse.allowedClaims);
            }
            this.canProcessScreeningCustomerDecisionForSubscriptions = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForSubscriptions);
            this.canProcessScreeningCustomerDecisionForRightStore = this.userClaimsService.hasPermission(Claims.CanProcessScreeningCustomerDecisionForRightStore);
            this.canLinkAutoScreeningWithFullVet = this.userClaimsService.hasPermission(Claims.CanLinkAutoScreeningWithFullVet);
            this.canViewRiskScoresInScreeningOverview = this.userClaimsService.hasPermission(Claims.CanViewRiskScoresInScreeningOverview);

            resolve();
        });
    }

    shouldShowSafetyScoreAndGhg(statusId: number) {
        return statusId == VetStatusEnum.AS_Passed ||
            statusId == VetStatusEnum.AS_ExceptionRequired ||
            statusId == VetStatusEnum.AS_Failed;
    }

    inspectionAsOf(): Date[] {
        return this.lastVetStatusHistories.map(x => this.completed(x.statusId) ? x.createdDate : null);
    }

    getDecisionStatus(customerDecisionStatusId: number) {
        if (customerDecisionStatusId) {
            if (customerDecisionStatusId == CustomerDecisionStatusEnum.Acceptable) {
                return "Exception Granted";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Unacceptable) {
                return "Exception Rejected";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Pending) {
                return "Pending";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Cancelled) {
                return "Cancelled";
            }
        }
    }

    navigateToFullVet() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Vet, { id: this.item.fullVetId });
    }

    openContactDetailsDialog() {
        this.item.initialEmailsForCorrespondence = [{ email: this.item.requestedByEmail, recipientType: "Requester" }]

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ContactDetailsDialog, {
            model: this.item,
            onConfirm: null
        }, true, false, false);
    }

    requestAFullVet() {
        const linkFullVetitem = CloneHelper.cloneObject(this.item);
        // set cloned autoscreen vetId to 0 so that we create a new one from autoscreen
        linkFullVetitem.vetId = 0;
        // init these two properties as false because they does not exist in autoscreens
        linkFullVetitem.vetForSTS = false;
        linkFullVetitem.charterTypeId = null;
        this.manageVesselService.showChooseDialog(
            {
                vessel: <VesselDetailModel>{
                    vetRequiresMoreInformationEmails: this.vessel.vetRequiresMoreInformationEmails,
                    documentOfComplianceEmail: this.vessel.documentOfComplianceEmail,
                    cargoTypeCategoryId: this.vessel.cargoTypeCategoryId,
                    vesselId: this.vessel.vesselId,
                    internationalMaritimeOrganizationNumber: this.vessel.internationalMaritimeOrganizationNumber,
                    name: this.vessel.name
                },
                vettingType: VettingType.FullVet,
                parentVetId: this.item.vetId,
                vet: linkFullVetitem,
                linkFullVetCheck: true

            });
    }

    private setValidator() {
        const rules = [{
            errorMessage: "Comments are required",
            parameters: null,
            property: "decisionComments",
            validationType: ValidationTypeEnum.Required
        }];

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Customer Decision Outcome",
            isCollection: false,
            nestedRulesHolder: [],
            rules: rules
        };

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    openRulesDialog(vetTaskKey: string) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.TradeCheckCTARulesDialog, {
            vet: this.item,
            selectedVetTaskKey: vetTaskKey
        }, true, true, false, "right");
    }
}
